/**
 * CoOccurrenceDetector — fuse simultaneous modality verdicts.
 *
 * Each modality (gaze-area, hand, body …) reports whether it currently
 * asserts a semantic verdict (see ModalitySemanticDetector). On its own a
 * single verdict is weak evidence; the demo's value is in the CO-OCCURRENCE
 * — "왼쪽 반복 응시" AND "상체 좌측 기울임" at the same time reads very
 * differently from either alone. This detector watches the set of active
 * verdicts and fires a fused event once ≥ minModalities are active within a
 * short sync window.
 *
 * A sync window (rather than requiring same-tick alignment) tolerates the
 * fact that modalities update on independent ticks and peak a few hundred
 * ms apart. The fused event is edge-tracked: `justActivated` / `justCleared`
 * let the caller log the fusion exactly once per episode.
 */

export interface ModalitySignal {
  /** Stable modality key, e.g. 'gaze_area'. */
  modality: string;
  /** Human-readable verdict label, e.g. '왼쪽 영역 반복 응시 (3회)'. */
  label: string;
  /** Whether this modality currently asserts a verdict. */
  active: boolean;
}

export interface CoOccurrence {
  active: boolean;
  /** Modality keys active within the sync window. */
  modalities: string[];
  /** Their verdict labels, in the same order. */
  labels: string[];
  /** When the current co-occurrence episode began (ms), or null. */
  since: number | null;
  /** True only on the tick the episode opened. */
  justActivated: boolean;
  /** True only on the tick the episode closed. */
  justCleared: boolean;
}

export class CoOccurrenceDetector {
  private syncWindowMs: number;
  private minModalities: number;
  private lastActive = new Map<string, { t: number; label: string }>();
  private episodeSince: number | null = null;

  constructor(syncWindowMs = 1_500, minModalities = 2) {
    this.syncWindowMs = syncWindowMs;
    this.minModalities = minModalities;
  }

  update(signals: ModalitySignal[], now: number): CoOccurrence {
    // Refresh last-active stamps for modalities asserting a verdict now.
    for (const s of signals) {
      if (s.active) this.lastActive.set(s.modality, { t: now, label: s.label });
    }
    // Drop stamps that have aged out of the sync window.
    const cutoff = now - this.syncWindowMs;
    for (const [k, v] of Array.from(this.lastActive.entries())) {
      if (v.t < cutoff) this.lastActive.delete(k);
    }

    const within = Array.from(this.lastActive.entries());
    const active = within.length >= this.minModalities;

    let justActivated = false;
    let justCleared = false;
    if (active && this.episodeSince == null) {
      this.episodeSince = now;
      justActivated = true;
    } else if (!active && this.episodeSince != null) {
      this.episodeSince = null;
      justCleared = true;
    }

    return {
      active,
      modalities: within.map(([k]) => k),
      labels: within.map(([, v]) => v.label),
      since: this.episodeSince,
      justActivated,
      justCleared,
    };
  }

  reset(): void {
    this.lastActive.clear();
    this.episodeSince = null;
  }
}
